import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  Contact_TasksFilter,
  Deals,
  DealsFilter,
  GetDealssDocument,
  GetDealssQuery,
  GetDealssQueryVariables,
  GetTaskssDocument,
  GetTaskssQuery,
  GetTaskssQueryVariables,
  GetTicketssDocument,
  GetTicketssQuery,
  GetTicketssQueryVariables,
  Tickets,
  TicketsFilter,
} from "@/src/gql/graphql";
import { ACTIVE_STAGES, IN_PROGRESS_DEAL_STAGES } from "@/lib/constants/deal-stages";
import { ACTIVE_TICKET_STATUSES } from "@/lib/constants/ticket-statuses";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

const MARKETING_TASK_TAG = "marketing";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Deals tagged as rentals are excluded entirely from the owner dashboard —
// they never count toward operations metrics, revenue, or any deal list here.
const RENTAL_DEAL_TAGS = new Set(["rental", "rentals"]);

function isRentalDeal(deal: Deals): boolean {
  return (deal.tags ?? []).some(
    (tag) => typeof tag === "string" && RENTAL_DEAL_TAGS.has(tag.trim().toLowerCase())
  );
}

function excludeRentalDeals(deals: Deals[]): Deals[] {
  return deals.filter((deal) => !isRentalDeal(deal));
}

function getMarketingTaskFilter(): Contact_TasksFilter {
  return {
    tags: {
      contains: [MARKETING_TASK_TAG],
    },
  };
}

function getOperationsTicketFilter(): TicketsFilter {
  return {
    status: { in: [...ACTIVE_TICKET_STATUSES] },
  };
}

function sevenDaysAgoIso(): string {
  return new Date(Date.now() - SEVEN_DAYS_MS).toISOString();
}

function getOverdueDealsFilter(cutoff: string): DealsFilter {
  return {
    and: [
      // Active sales pipeline (was a hand-rolled list missing `discovery` +
      // `contract_signed`, so deals in those stages were never flagged overdue).
      { stage: { in: [...ACTIVE_STAGES] } },
      { updated_at: { lt: cutoff } },
    ],
  };
}

function getInProgressDealsFilter(): DealsFilter {
  return {
    stage: { in: [...IN_PROGRESS_DEAL_STAGES] },
  };
}

function getOverdueTasksFilter(cutoff: string): Contact_TasksFilter {
  return {
    and: [
      { completed_at: { is: "NULL" } },
      { updated_at: { lt: cutoff } },
    ],
  };
}

function getTicketsWaitingOnCustomerFilter(): TicketsFilter {
  return {
    status: { eq: "waiting_for_customer" },
  };
}

export function useOwnerDashboardMarketingTasks(limit: number = 100) {
  const variables = useMemo<GetTaskssQueryVariables>(
    () => ({
      first: limit,
      filter: getMarketingTaskFilter(),
    }),
    [limit]
  );

  const result = useQuery<GetTaskssQuery, GetTaskssQueryVariables>(GetTaskssDocument, {
    variables,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const tasks = useMemo<ContactTaskRow[]>(() => {
    return (
      result.data?.contact_tasksCollection?.edges
        ?.map((edge) => edge?.node)
        .filter((node) => node !== null && node !== undefined) ?? []
    );
  }, [result.data]);

  return {
    ...result,
    tasks,
    totalCount: result.data?.contact_tasksCollection?.totalCount ?? 0,
  };
}

export function useOwnerDashboardOperationsTickets(limit: number = 100) {
  const variables = useMemo<GetTicketssQueryVariables>(
    () => ({
      first: limit,
      filter: getOperationsTicketFilter(),
    }),
    [limit]
  );

  const result = useQuery<GetTicketssQuery, GetTicketssQueryVariables>(GetTicketssDocument, {
    variables,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const tickets = useMemo<Tickets[]>(() => {
    return (
      result.data?.ticketsCollection?.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is Tickets => node !== null && node !== undefined) ?? []
    );
  }, [result.data]);

  return {
    ...result,
    tickets,
    totalCount: result.data?.ticketsCollection?.totalCount ?? 0,
  };
}

export function useOwnerDashboardOverdueDeals(limit: number = 50) {
  const cutoff = useMemo(() => sevenDaysAgoIso(), []);

  const variables = useMemo<GetDealssQueryVariables>(
    () => ({
      first: limit,
      filter: getOverdueDealsFilter(cutoff),
      orderBy: [{ updated_at: "AscNullsLast" }],
    }),
    [limit, cutoff]
  );

  const result = useQuery<GetDealssQuery, GetDealssQueryVariables>(GetDealssDocument, {
    variables,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const deals = useMemo<Deals[]>(() => {
    return excludeRentalDeals(
      result.data?.dealsCollection?.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is Deals => node !== null && node !== undefined) ?? []
    );
  }, [result.data]);

  return {
    ...result,
    deals,
    totalCount: deals.length,
    cutoff,
  };
}

export function useOwnerDashboardInProgressDeals(limit: number = 50) {
  const variables = useMemo<GetDealssQueryVariables>(
    () => ({
      first: limit,
      filter: getInProgressDealsFilter(),
      orderBy: [{ updated_at: "AscNullsLast" }],
    }),
    [limit]
  );

  const result = useQuery<GetDealssQuery, GetDealssQueryVariables>(GetDealssDocument, {
    variables,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const deals = useMemo<Deals[]>(() => {
    return excludeRentalDeals(
      result.data?.dealsCollection?.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is Deals => node !== null && node !== undefined) ?? []
    );
  }, [result.data]);

  return {
    ...result,
    deals,
    totalCount: deals.length,
  };
}

export function useOwnerDashboardOverdueTasks(limit: number = 50) {
  const cutoff = useMemo(() => sevenDaysAgoIso(), []);

  const variables = useMemo<GetTaskssQueryVariables>(
    () => ({
      first: limit,
      filter: getOverdueTasksFilter(cutoff),
    }),
    [limit, cutoff]
  );

  const result = useQuery<GetTaskssQuery, GetTaskssQueryVariables>(GetTaskssDocument, {
    variables,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const tasks = useMemo<ContactTaskRow[]>(() => {
    return (
      result.data?.contact_tasksCollection?.edges
        ?.map((edge) => edge?.node)
        .filter((node) => node !== null && node !== undefined) ?? []
    );
  }, [result.data]);

  return {
    ...result,
    tasks,
    totalCount: result.data?.contact_tasksCollection?.totalCount ?? 0,
    cutoff,
  };
}

export function useOwnerDashboardTicketsWaitingOnCustomer(limit: number = 50) {
  const variables = useMemo<GetTicketssQueryVariables>(
    () => ({
      first: limit,
      filter: getTicketsWaitingOnCustomerFilter(),
    }),
    [limit]
  );

  const result = useQuery<GetTicketssQuery, GetTicketssQueryVariables>(GetTicketssDocument, {
    variables,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const tickets = useMemo<Tickets[]>(() => {
    return (
      result.data?.ticketsCollection?.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is Tickets => node !== null && node !== undefined) ?? []
    );
  }, [result.data]);

  return {
    ...result,
    tickets,
    totalCount: result.data?.ticketsCollection?.totalCount ?? 0,
  };
}
